/**
 * ProjectDashboardPage — dual-pane file manager for a project.
 *
 * Left:   FolderTreePanel (w-64, border-r) with Collections
 * Right:  UploadOverlay > FilterBar > AssetGrid (flex-1)
 * Far-right: AssetDetailPanel (w-80) when 1 asset selected
 * Top:    Breadcrumbs | Search | ViewToggle | Upload button
 *
 * Uses FileManagerContext as single source of truth for the folder tree
 * so the Sidebar and this page stay in sync.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { isVideoAsset } from '../utils/mediaType'
import {
  ArrowLeft, Edit2, Check, X, Upload, Trash2, Download, Move,
  PanelLeftClose, PanelLeftOpen, Filter, Cpu, Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import { assetApi, collectionApi } from '../services/assetFolderApi'
import type { FolderNode, Asset, ViewMode, SortField, SortDir } from '../types/fileManager'
import { FolderTreePanel, AssetGrid, BreadcrumbNav, UploadOverlay, MoveFolderModal } from '../components/FileManager'
import AssetDetailPanel from '../components/FileManager/AssetDetailPanel'
import FilterBar from '../components/FileManager/FilterBar'
import { type ActiveFilters, EMPTY_FILTERS, hasActiveFilters } from '../components/FileManager/filters'
import CreateCollectionModal from '../components/FileManager/CreateCollectionModal'
import type { MenuItem } from '../components/FileManager/ContextMenu'
import { useFileManager } from '../contexts/fileManager'
import { apiErrorDetail, normalizeErrorMessage } from '../utils/dataValidator'

interface Project {
  id: string
  name: string
  description: string | null
  status: string
  created_at: string
  updated_at: string | null
  asset_count: number
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Find a folder node by id anywhere in the tree. */
function findNodeById(nodes: FolderNode[], id: number): FolderNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    const found = findNodeById(n.children || [], id)
    if (found) return found
  }
  return null
}

/** Find the first leaf folder (source_bucket or custom) via DFS. */
/**
 * Merge freshly-fetched assets with the previous state, preserving stable
 * `thumbnail_url` values so presigned URL regeneration doesn't cause every
 * image in the grid to flash/reload.
 */
function mergeAssets(prev: Asset[], next: Asset[]): Asset[] {
  if (prev.length === 0) return next
  const prevMap = new Map(prev.map(a => [a.id, a]))
  return next.map(a => {
    const old = prevMap.get(a.id)
    if (old?.thumbnail_url && a.thumbnail_url) {
      return { ...a, thumbnail_url: old.thumbnail_url }
    }
    return a
  })
}

// ─────────────────────────────────────────────────────────────────────────────

export default function ProjectDashboardPage() {
  const { projectId } = useParams<{ projectId: string }>()

  // Shared context — single source of truth for tree + selectedFolder + collections
  const {
    folderTree, rootFolderId, selectedFolderId, setSelectedFolderId, refreshTree,
    collections, activeCollectionId, setActiveCollectionId, refreshCollections,
  } = useFileManager()

  // Project
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')

  // Mobile tree toggle
  const [showTreeMobile, setShowTreeMobile] = useState(false)

  // Assets
  const [assets, setAssets] = useState<Asset[]>([])
  const [assetsTotal, setAssetsTotal] = useState(0)
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<number>>(new Set())

  // View controls
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('upload_time')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Button upload progress
  const [btnUploading, setBtnUploading] = useState(false)
  const [btnProgress, setBtnProgress] = useState(0)
  const [btnSpeed, setBtnSpeed] = useState(0)

  // Filters
  const [filters, setFilters] = useState<ActiveFilters>(EMPTY_FILTERS)
  const [showFilters, setShowFilters] = useState(false)

  // Detail panel
  const [detailAsset, setDetailAsset] = useState<Asset | null>(null)

  // Modals
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [showCreateCollection, setShowCreateCollection] = useState(false)

  // Abort controller for asset fetches
  const abortRef = useRef<AbortController | null>(null)

  // Collect all known tags from current assets
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    assets.forEach(a => a.tags?.forEach(t => tagSet.add(t)))
    return Array.from(tagSet).sort()
  }, [assets])

  // ── Debounce search ──
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  // ── Fetch project ──
  const fetchProject = useCallback(async () => {
    if (!projectId) return
    try {
      const res = await apiClient.get(`/projects/${projectId}`)
      setProject(res.data)
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load project'))
    }
  }, [projectId])

  // ── Fetch assets ──
  const fetchAssets = useCallback(async () => {
    if (!projectId) return

    // If viewing a collection, use collection assets endpoint
    if (activeCollectionId) {
      setAssetsLoading(true)
      try {
        const data = await collectionApi.getAssets(activeCollectionId, { limit: 200 })
        setAssets(prev => mergeAssets(prev, data.assets))
        setAssetsTotal(data.total)
      } catch {
        // silent
      } finally {
        setAssetsLoading(false)
      }
      return
    }

    // Cancel previous in-flight request
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setAssetsLoading(true)
    try {
      const opts: Parameters<typeof assetApi.list>[1] = {
        folderId: debouncedSearch ? undefined : (selectedFolderId ?? undefined),
        search: debouncedSearch || undefined,
        limit: 200,
      }
      // Apply metadata filters
      if (filters.editorial_status.length > 0) opts.editorial_status = filters.editorial_status.join(',')
      if (filters.priority.length > 0) opts.priority = filters.priority.join(',')
      if (filters.tags.length > 0) opts.tags = filters.tags.join(',')
      if (filters.asset_type.length > 0) opts.asset_type = filters.asset_type.join(',')

      const data = await assetApi.list(projectId, opts)
      if (!controller.signal.aborted) {
        setAssets(prev => mergeAssets(prev, data.assets))
        setAssetsTotal(data.total)
      }
    } catch (err) {
      if ((err as { name?: unknown })?.name !== 'CanceledError' && !controller.signal.aborted) {
        // Silent — user will see empty state
      }
    } finally {
      if (!controller.signal.aborted) {
        setAssetsLoading(false)
      }
    }
  }, [projectId, selectedFolderId, debouncedSearch, filters, activeCollectionId])

  // ── Initial load ──
  useEffect(() => {
    setLoading(true)
    fetchProject().finally(() => setLoading(false))
  }, [fetchProject])

  // ── Refetch assets when folder, search, filters, or collection changes ──
  useEffect(() => {
    fetchAssets()
    return () => { abortRef.current?.abort() }
  }, [fetchAssets])

  // ── Sort assets client-side (memoized) ──
  const sortedAssets = useMemo(() => {
    return [...assets].sort((a, b) => {
      let cmp = 0
      if (sortField === 'upload_time') {
        cmp = (a.upload_time ?? '').localeCompare(b.upload_time ?? '')
      } else if (sortField === 'original_filename') {
        cmp = a.original_filename.localeCompare(b.original_filename)
      } else if (sortField === 'file_size') {
        cmp = a.file_size - b.file_size
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [assets, sortField, sortDir])

  // ── Mutations ──
  const refreshAll = useCallback(() => {
    refreshTree()
    fetchAssets()
    fetchProject() // asset_count may have changed
  }, [refreshTree, fetchAssets, fetchProject])

  const handleUpload = useCallback(async (
    files: File[],
    onProgress?: (pct: number) => void,
    onSpeed?: (mbps: number) => void,
  ) => {
    if (!projectId) return

    // If a leaf folder is selected, use explicit folder_id. Otherwise auto-route.
    let folderId: number | undefined
    if (selectedFolderId) {
      const folder = findNodeById(folderTree, selectedFolderId)
      if (folder && folder.folder_type === 'source_bucket') {
        folderId = selectedFolderId
      } else if (folder && folder.folder_type === 'custom' && !(folder.children?.length)) {
        folderId = selectedFolderId
      }
    }

    try {
      const result = await assetApi.uploadDirect(projectId, files, { folderId, onProgress, onSpeed })
      toast.success(`Uploaded ${result.files.length} file(s)`)
      refreshAll()
    } catch (err) {
      toast.error(`Upload failed: ${normalizeErrorMessage(err, 'Unknown error')}`)
    }
  }, [projectId, selectedFolderId, folderTree, refreshAll])

  const handleBulkDelete = useCallback(async () => {
    if (selectedAssetIds.size === 0) return
    if (!confirm(`Delete ${selectedAssetIds.size} file(s)? This cannot be undone.`)) return
    try {
      await assetApi.bulkDelete([...selectedAssetIds])
      toast.success(`Deleted ${selectedAssetIds.size} file(s)`)
      setSelectedAssetIds(new Set())
      setDetailAsset(null)
      refreshAll()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete'))
    }
  }, [selectedAssetIds, refreshAll])

  const handleBulkMove = useCallback(async (targetFolderId: number) => {
    try {
      await assetApi.bulkMove([...selectedAssetIds], targetFolderId)
      toast.success(`Moved ${selectedAssetIds.size} file(s)`)
      setSelectedAssetIds(new Set())
      refreshAll()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to move'))
      throw err
    }
  }, [selectedAssetIds, refreshAll])

  const handleDeleteSingle = useCallback(async (asset: Asset) => {
    try {
      await assetApi.delete(asset.id)
      toast.success('File deleted')
      if (detailAsset?.id === asset.id) setDetailAsset(null)
      refreshAll()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete'))
    }
  }, [refreshAll, detailAsset])

  const handleDownload = useCallback(async (asset: Asset) => {
    try {
      const { url } = await assetApi.getDownloadUrl(asset.id)
      window.open(url, '_blank')
    } catch {
      toast.error('Failed to get download link')
    }
  }, [])

  // ── Quick status change from AssetCard ──
  const handleStatusChange = useCallback(async (assetId: number, status: string) => {
    try {
      const updated = await assetApi.update(assetId, { editorial_status: status })
      setAssets(prev => prev.map(a => a.id === assetId ? updated : a))
      if (detailAsset?.id === assetId) setDetailAsset(updated)
    } catch {
      toast.error('Failed to update status')
    }
  }, [detailAsset])

  // ── Asset updated from detail panel ──
  const handleAssetUpdated = useCallback((updated: Asset) => {
    setAssets(prev => prev.map(a => a.id === updated.id ? updated : a))
    setDetailAsset(updated)
  }, [])

  // ── Selection helpers ──
  const toggleSelect = useCallback((id: number, additive: boolean) => {
    setSelectedAssetIds(prev => {
      const next = new Set(additive ? prev : [])
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    // Open detail panel for single selection
    if (!additive) {
      const asset = assets.find(a => a.id === id)
      if (asset) setDetailAsset(prev => prev?.id === id ? null : asset)
    }
  }, [assets])

  const selectAll = useCallback(
    () => setSelectedAssetIds(new Set(sortedAssets.map(a => a.id))),
    [sortedAssets],
  )
  const clearSelection = useCallback(() => {
    setSelectedAssetIds(new Set())
    setDetailAsset(null)
  }, [])

  // ── Asset context menu builder ──
  const buildAssetContextItems = useCallback((asset: Asset): MenuItem[] => {
    return [
      {
        label: 'Download',
        icon: <Download className="w-4 h-4" />,
        onClick: () => handleDownload(asset),
      },
      {
        label: 'Move to...',
        icon: <Move className="w-4 h-4" />,
        onClick: () => {
          setSelectedAssetIds(prev =>
            prev.has(asset.id) ? prev : new Set([asset.id]),
          )
          setShowMoveModal(true)
        },
      },
      {
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        danger: true,
        onClick: () => {
          if (selectedAssetIds.size > 1 && selectedAssetIds.has(asset.id)) {
            handleBulkDelete()
          } else {
            handleDeleteSingle(asset)
          }
        },
      },
    ]
  }, [handleDownload, handleBulkDelete, handleDeleteSingle, selectedAssetIds])

  // ── Sort toggle ──
  const handleSortChange = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir(field === 'original_filename' ? 'asc' : 'desc')
    }
  }, [sortField])

  // ── Create collection from current filters ──
  const handleCreateCollection = useCallback(async (data: {
    name: string; description: string; icon: string; color: string; filter_config: Record<string, unknown>
  }) => {
    if (!projectId) return
    try {
      await collectionApi.create({
        project_id: projectId,
        name: data.name,
        description: data.description || undefined,
        icon: data.icon,
        color: data.color,
        filter_config: data.filter_config,
      })
      toast.success('Collection created')
      refreshCollections()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create collection'))
      throw err
    }
  }, [projectId, refreshCollections])

  // ── Drag & drop: assets onto folders ──
  const handleAssetDragStart = useCallback((e: React.DragEvent, asset: Asset) => {
    const ids = selectedAssetIds.has(asset.id) ? [...selectedAssetIds] : [asset.id]
    e.dataTransfer.setData('application/x-asset-ids', JSON.stringify(ids))
    e.dataTransfer.effectAllowed = 'move'
  }, [selectedAssetIds])

  const handleFolderDragOver = useCallback((e: React.DragEvent, _node: FolderNode) => {
    if (e.dataTransfer.types.includes('application/x-asset-ids')) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }, [])

  const handleFolderDrop = useCallback(async (e: React.DragEvent, node: FolderNode) => {
    const raw = e.dataTransfer.getData('application/x-asset-ids')
    if (!raw) return
    if (node.folder_type !== 'source_bucket' && node.folder_type !== 'custom') {
      toast.error('Assets can only be dropped into leaf folders')
      return
    }
    try {
      const ids: number[] = JSON.parse(raw)
      await assetApi.bulkMove(ids, node.id)
      toast.success(`Moved ${ids.length} file(s) to ${node.name}`)
      setSelectedAssetIds(new Set())
      refreshAll()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to move'))
    }
  }, [refreshAll])

  // ── Bulk analyze ──
  const videoAssetsToAnalyze = useMemo(() =>
    sortedAssets.filter(a =>
      selectedAssetIds.has(a.id) &&
      isVideoAsset(a) &&
      (a.status === 'uploaded' || a.status === 'failed')
    ),
    [sortedAssets, selectedAssetIds],
  )

  const handleBulkAnalyze = useCallback(async () => {
    if (videoAssetsToAnalyze.length === 0) return
    const ids = videoAssetsToAnalyze.map(a => a.id)
    toast.success(`Starting analysis for ${ids.length} video(s)...`)
    // Mark locally as ingesting immediately
    setAssets(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: 'ingesting' } : a))
    const results = await Promise.allSettled(
      ids.map(id => apiClient.post(`/ingest/${id}/start`)),
    )
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed > 0) toast.error(`${failed} asset(s) failed to start analysis`)
  }, [videoAssetsToAnalyze])

  // ── Auto-poll while any asset is ingesting ──
  const hasIngesting = assets.some(a => a.status === 'ingesting')
  useEffect(() => {
    if (!hasIngesting) return
    const poll = setInterval(() => fetchAssets(), 4000)
    return () => clearInterval(poll)
  }, [hasIngesting, fetchAssets])

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (editing) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedAssetIds.size > 0) {
        e.preventDefault()
        handleBulkDelete()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        selectAll()
      }
      if (e.key === 'Escape') {
        if (detailAsset) setDetailAsset(null)
        else if (selectedAssetIds.size > 0) clearSelection()
        else setShowTreeMobile(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [selectedAssetIds, editing, handleBulkDelete, selectAll, clearSelection, detailAsset])

  // ── Project edit ──
  const saveEdit = async () => {
    if (!projectId || !editName.trim()) return
    try {
      const res = await apiClient.patch(`/projects/${projectId}`, {
        name: editName.trim(),
        description: editDesc.trim() || null,
      })
      setProject(res.data)
      setEditing(false)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update project'))
    }
  }

  const startEdit = () => {
    if (!project) return
    setEditName(project.name)
    setEditDesc(project.description || '')
    setEditing(true)
  }

  // ── Derived: current folder for breadcrumbs ──
  const currentFolder = selectedFolderId ? findNodeById(folderTree, selectedFolderId) : null
  const activeCollection = activeCollectionId ? collections.find(c => c.id === activeCollectionId) : null

  // ── Render ──
  if (loading) return <div className="p-8 text-[var(--rq-text-soft)]">Loading workspace...</div>
  if (error) return <div className="p-8 text-red-400">{error}</div>
  if (!project) return <div className="p-8 text-[var(--rq-text-soft)]">Workspace not found</div>

  return (
    <div className="flex flex-col h-[calc(100vh-theme(spacing.16))] md:h-screen">
      {/* Top bar */}
      <div className="flex-shrink-0 border-b border-white/10 px-4 md:px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/projects" className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </Link>

          {/* Mobile tree toggle */}
          <button
            onClick={() => setShowTreeMobile(!showTreeMobile)}
            className="md:hidden p-1.5 rounded-lg hover:bg-white/5 text-[var(--rq-text-soft)]"
          >
            {showTreeMobile ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
          </button>

          {/* Project name / edit */}
          {editing ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                autoFocus
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="rq-input-dark text-sm font-semibold flex-1 min-w-0"
                onKeyDown={e => e.key === 'Enter' && saveEdit()}
              />
              <button onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300 p-1"><Check className="w-4 h-4" /></button>
              <button onClick={() => setEditing(false)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={startEdit} className="flex items-center gap-2 group min-w-0">
              <h1 className="text-sm font-semibold text-[var(--rq-text)] truncate">{project.name}</h1>
              <Edit2 className="w-3.5 h-3.5 text-[var(--rq-text-soft)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </button>
          )}

          {/* Breadcrumbs or collection name */}
          <div className="hidden md:flex items-center">
            <span className="text-[var(--rq-text-soft)] mx-2">/</span>
            {activeCollection ? (
              <span className="text-xs text-[var(--rq-blue)] font-medium">{activeCollection.name}</span>
            ) : (
              <BreadcrumbNav
                pathCache={currentFolder?.path_cache ?? null}
                folderTree={folderTree}
                onNavigate={(id) => setSelectedFolderId(id)}
              />
            )}
          </div>

          <div className="flex-1" />

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
              hasActiveFilters(filters)
                ? 'bg-[var(--rq-blue)]/15 text-[var(--rq-blue)]'
                : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Filters</span>
          </button>

          {/* Bulk actions */}
          {selectedAssetIds.size > 0 && (
            <div className="flex items-center gap-2">
              {videoAssetsToAnalyze.length > 0 && (
                <button
                  onClick={handleBulkAnalyze}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-400/10 rounded-lg transition-colors"
                >
                  <Cpu className="w-3.5 h-3.5" /> Analyze ({videoAssetsToAnalyze.length})
                </button>
              )}
              <button
                onClick={() => setShowMoveModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5 rounded-lg transition-colors"
              >
                <Move className="w-3.5 h-3.5" /> Move
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          )}

          {/* Upload button */}
          <label className={`rq-btn-primary px-3 py-1.5 text-sm ${btnUploading ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
            {btnUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="hidden sm:inline">{btnUploading ? 'Uploading...' : 'Upload'}</span>
            <input
              type="file"
              multiple
              className="hidden"
              disabled={btnUploading}
              onChange={async e => {
                if (e.target.files) {
                  setBtnUploading(true)
                  setBtnProgress(0)
                  setBtnSpeed(0)
                  try { await handleUpload(Array.from(e.target.files), setBtnProgress, setBtnSpeed) } finally { setBtnUploading(false); setBtnProgress(0); setBtnSpeed(0) }
                }
                e.target.value = ''
              }}
            />
          </label>
        </div>
      </div>

      {/* Main area: tree + grid + detail */}
      <div className="flex flex-1 min-h-0">
        {/* Folder tree — desktop always, mobile toggle */}
        <div className={`${showTreeMobile ? 'block' : 'hidden'} md:block w-64 flex-shrink-0 border-r border-white/10 overflow-hidden`}>
          <FolderTreePanel
            projectId={projectId!}
            tree={folderTree}
            rootFolderId={rootFolderId}
            selectedFolderId={selectedFolderId}
            onSelectFolder={(id) => {
              setSelectedFolderId(id)
              setSelectedAssetIds(new Set())
              setDetailAsset(null)
              setShowTreeMobile(false)
            }}
            onTreeChange={refreshAll}
            onDragOver={handleFolderDragOver}
            onDrop={handleFolderDrop}
            collections={collections}
            activeCollectionId={activeCollectionId}
            onSelectCollection={(id) => {
              setActiveCollectionId(id)
              setSelectedAssetIds(new Set())
              setDetailAsset(null)
            }}
            onCollectionsChange={refreshCollections}
          />
        </div>

        {/* Asset grid with upload overlay */}
        <UploadOverlay
          disabled={!projectId || !!activeCollectionId}
          onUpload={handleUpload}
          uploading={btnUploading}
          uploadProgress={btnProgress}
          uploadSpeedMBps={btnSpeed}
        >
          {/* Filter bar */}
          {showFilters && (
            <FilterBar
              filters={filters}
              onChange={setFilters}
              onSaveAsCollection={() => setShowCreateCollection(true)}
              allTags={allTags}
            />
          )}

          <AssetGrid
            assets={sortedAssets}
            total={assetsTotal}
            loading={assetsLoading}
            viewMode={viewMode}
            setViewMode={setViewMode}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedIds={selectedAssetIds}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
            onAssetContextMenu={buildAssetContextItems}
            onDragStart={handleAssetDragStart}
            onStatusChange={handleStatusChange}
            sortField={sortField}
            sortDir={sortDir}
            onSortChange={handleSortChange}
          />
        </UploadOverlay>

        {/* Asset detail panel */}
        {detailAsset && (
          <AssetDetailPanel
            asset={detailAsset}
            onClose={() => setDetailAsset(null)}
            onAssetUpdated={handleAssetUpdated}
            allTags={allTags}
          />
        )}
      </div>

      {/* Move modal */}
      {showMoveModal && (
        <MoveFolderModal
          title={`Move ${selectedAssetIds.size} file(s)`}
          tree={folderTree}
          onClose={() => setShowMoveModal(false)}
          onSelect={handleBulkMove}
        />
      )}

      {/* Create collection modal */}
      {showCreateCollection && (
        <CreateCollectionModal
          onClose={() => setShowCreateCollection(false)}
          onCreate={handleCreateCollection}
          initialFilters={filters}
        />
      )}
    </div>
  )
}
